import { usePastillesDeLHote, type PortesDeConversations } from './pastilles.js';
import { useMemo } from 'react';
import type { RefConversation, TakiSdk } from '@taki/sdk';
import type { Conversation } from '@taki/data-model';
import type { AncreVue, ConversationVue, ConversationsCentrePort, FicheLueVue } from '@taki/ui';

/**
 * LES CONVERSATIONS DU CENTRE DE COMMUNICATION, reliees au shell par l'hote
 * (le shell n'a pas le SDK). Canaux, messages directs et chatter viennent de
 * `sdk.conversations` ; le fil et le composeur du socle lisent et ecrivent par
 * `sdk.messages` ; les non lus par `sdk.conversations.lecture` (contrat
 * `docs/socle/conversations.md`).
 */
const CHAMPS_STANDARD = new Set(['orgId', 'type', 'createdAt', 'updatedAt', 'createdBy', 'updatedBy', 'archivedAt', 'deletedAt', 'schemaVersion', 'customFields', 'acces', 'personId', 'userId']);

function millis(value: unknown): number | null {
  if (value instanceof Date) return value.getTime();
  if (value != null && typeof value === 'object' && 'toMillis' in value && typeof value.toMillis === 'function') return value.toMillis() as number;
  return null;
}

export function vueDeConversation(c: Conversation): ConversationVue {
  const acces = c.acces;
  const ancre: AncreVue | null = acces?.visibilite === 'ancre' ? acces.ancre : c.ancre ?? null;
  return {
    id: c.id,
    type: c.type,
    name: c.name,
    prive: acces?.visibilite === 'prive',
    participants: acces?.visibilite === 'prive' ? [...acces.participants] : [],
    proprietaire: acces?.visibilite === 'prive' ? acces.proprietaire : null,
    ancre,
    modifieLe: millis((c as { updatedAt?: unknown }).updatedAt),
  };
}

/** Le titre d'une fiche lue a froid : nom, titre, ou prenom et nom, sinon l'id. */
function titreDeFiche(record: Record<string, unknown>, id: string): string {
  for (const clef of ['name', 'title', 'displayName', 'label']) {
    const valeur = record[clef];
    if (typeof valeur === 'string' && valeur.trim()) return valeur.trim();
  }
  /* LE TITRE D'UNE FICHE DECLAREE vit souvent dans le sac (`customFields.libelle`
     d'un to-do, d'une tache) : sans lui, le suivi d'une fiche s'affichait par
     son id chez la personne mentionnee. Memes clefs, plus les noms francais. */
  const sac = record['customFields'];
  if (sac !== null && typeof sac === 'object') {
    for (const clef of ['libelle', 'nom', 'titre', 'name', 'title', 'label']) {
      const valeur = (sac as Record<string, unknown>)[clef];
      if (typeof valeur === 'string' && valeur.trim()) return valeur.trim();
    }
  }
  const prenom = typeof record['firstName'] === 'string' ? record['firstName'] : '';
  const nom = typeof record['lastName'] === 'string' ? record['lastName'] : '';
  return `${prenom} ${nom}`.trim() || id;
}

function texteDeValeur(valeur: unknown): string | null {
  if (valeur == null || valeur === '') return null;
  if (typeof valeur === 'string') return valeur.length > 120 ? `${valeur.slice(0, 117)}...` : valeur;
  if (typeof valeur === 'number') return new Intl.NumberFormat('fr-FR').format(valeur);
  if (typeof valeur === 'boolean') return valeur ? 'oui' : 'non';
  if (valeur instanceof Date) return new Intl.DateTimeFormat('fr-FR', { dateStyle: 'medium' }).format(valeur);
  const date = millis(valeur);
  if (date !== null) return new Intl.DateTimeFormat('fr-FR', { dateStyle: 'medium' }).format(new Date(date));
  if (Array.isArray(valeur)) return valeur.filter((v) => typeof v === 'string').join(', ') || null;
  return null;
}

/** `portes` : une liste dont la porte est fermee ne s'ecoute pas, elle est vide (les rules la refuseraient). */
export function useConversationsCentreDeLHote(sdk: TakiSdk, uid: string | null, email: string | null, portes: PortesDeConversations): ConversationsCentrePort {
  const { pastilles, fil } = usePastillesDeLHote(sdk, uid, email, portes);
  const { canaux, directs } = portes;
  const sources = useMemo<ConversationsCentrePort>(() => ({
    observerSuivies: (onChange, onError) => sdk.conversations.observerSuivies(l => onChange(l.map(vueDeConversation)), onError),
    observerCanaux: (onChange, onError) => {
      if (!canaux) { onChange([]); return () => undefined; }
      return sdk.conversations.observerCanaux((liste) => onChange(liste.map(vueDeConversation)), onError);
    },
    observerDirects: (onChange, onError) => {
      if (!directs) { onChange([]); return () => undefined; }
      return sdk.conversations.observerDirects((liste) => onChange(liste.map(vueDeConversation)), onError);
    },
    lire: async (id) => { const c = await sdk.conversations.get(id); return c ? vueDeConversation(c) : null; },
    creerCanal: async (input) => vueDeConversation(await sdk.conversations.creerCanal({
      nom: input.nom, ...(input.description ? { description: input.description } : {}),
      ...(input.prive ? { prive: true } : {}), ...(input.participants ? { participants: input.participants } : {}),
    })),
    creerDirect: async (personIds) => vueDeConversation(await sdk.conversations.creerDirect(personIds)),
    chatterDe: async (ancre) => { const c = await sdk.conversations.chatterDe(ancre); return c ? vueDeConversation(c) : null; },
    ouvrirChatter: async (ancre) => vueDeConversation(await sdk.conversations.ouvrirChatter(ancre)),
    // Tour 3 du contrat : la base de la fiche a-t-elle le chatter allume (lu par le serveur).
    chatterActif: (ancre) => sdk.conversations.chatterActif(ancre),
    lecture: {
      observer: (onChange, onError) => sdk.conversations.lecture.observer(onChange, onError),
      marquerLu: (id, jusquA) => sdk.conversations.lecture.marquerLu(id, jusquA),
      nonLus: (id, lu) => sdk.conversations.lecture.nonLus(id, lu),
    },
    fil,
    renommer: async (id, nom) => { await sdk.conversations.renommer(id, nom); },
    archiver: (id) => sdk.conversations.archiver(id),
    peutGerer: async (id) => { const c = await sdk.conversations.get(id); return c ? sdk.conversations.peutGerer(c) : false; },
    epingles: {
      observer: (onChange, onError) => sdk.conversations.preferences.observerEpinglees((refs) => onChange([...refs]), onError),
      epingler: (ref) => sdk.conversations.preferences.epingler(ref as RefConversation),
      desepingler: (ref) => sdk.conversations.preferences.desepingler(ref as RefConversation),
    },
    lireFiche: async (family, recordId): Promise<FicheLueVue | null> => {
      let depot: ReturnType<TakiSdk['famille']>;
      try { depot = sdk.famille(family); } catch { return null; }
      const record = await depot.get(recordId);
      if (record === null) return null;
      // Les natifs puis le sac `customFields`, sous leur CHEMIN : le centre les nomme avec les libelles de la porte.
      const sac = record['customFields'] != null && typeof record['customFields'] === 'object' ? record['customFields'] as Record<string, unknown> : {};
      const champs = [
        ...depot.champs().filter((champ) => !CHAMPS_STANDARD.has(champ.key)).map((champ) => ({ cle: champ.key, valeur: texteDeValeur(record[champ.key]) })),
        ...Object.entries(sac).map(([clef, valeur]) => ({ cle: `customFields.${clef}`, valeur: texteDeValeur(valeur) })),
      ]
        .filter((champ): champ is { cle: string; valeur: string } => champ.valeur !== null)
        .map((champ) => ({ ...champ, libelle: champ.cle }))
        .slice(0, 40);
      return { titre: titreDeFiche(record, recordId), ...(typeof record.type === 'string' ? { type: record.type } : {}), ...(record['archivedAt'] != null ? { archivee: true } : {}), champs };
    },
  }), [sdk, fil, canaux, directs]);
  return useMemo(() => ({ ...sources, sources, pastilles }), [sources, pastilles]);
}
